/* @jsx h */
import { defineComponent, h } from "vue";
import { PaymentsSummaryReport } from "@lemmonhq/types";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";

export const PaymentMethods = defineComponent<{
  data: PaymentsSummaryReport;
  currency: string;
}>({
  name: "PaymentMethods",
  props: ["data", "currency"],
  setup(props) {
    const totalPayments =
      props.data.cash_amount +
      props.data.card_amount +
      props.data.twint_amount +
      props.data.manual_amount +
      props.data.online_amount;
    const { t } = useLocalization();
    return () => (
      <div class="card transition-shadow border rounded-lg md:rounded-box">
        <div class="card-body p-4 md:p-6">
          <h3 class="card-title flex items-center gap-2 text-accent font-normal">
            <span class="material-symbols-outlined">
              account_balance_wallet
            </span>
            {t("reports.dashboard.payment_methods")}
          </h3>
          <div class="space-y-4 md:space-y-6 pt-4 md:pt-6">
            <div class="space-y-4">
              {/* Card */}
              <div>
                <div class="flex justify-between mb-1">
                  <div class="flex items-center gap-2 text-accent">
                    <span class="material-symbols-outlined">credit_card</span>
                    <span>Card</span>
                  </div>
                  {totalPayments > 0 && (
                    <span class="text-sm text-accent">
                      {(
                        ((props.data.card_amount + props.data.twint_amount) /
                          totalPayments) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  )}
                </div>
                <progress
                  class="progress progress-success w-full"
                  value={props.data.card_amount + props.data.twint_amount}
                  max={totalPayments}
                ></progress>
                <div class="flex justify-between mt-1 text-sm opacity-60">
                  <span>
                    {props.data.card_count + props.data.twint_count} payments
                  </span>
                  <span>
                    {formatPriceInLocale(
                      props.data.card_amount + props.data.twint_amount,
                      true,
                      2,
                      props.currency,
                    )}
                  </span>
                </div>
              </div>

              {/* Twint */}
              {false && (
                <div>
                  <div class="flex justify-between mb-1">
                    <div class="flex items-center gap-2 text-accent">
                      <span class="material-symbols-outlined">hexagon</span>
                      <span>Twint</span>
                    </div>
                    {totalPayments > 0 && (
                      <span class="text-sm text-accent">
                        {(
                          (props.data.twint_amount / totalPayments) *
                          100
                        ).toFixed(1)}
                        %
                      </span>
                    )}
                  </div>
                  <progress
                    class="progress progress-primary w-full"
                    value={props.data.twint_amount}
                    max={totalPayments}
                  ></progress>
                  <div class="flex justify-between mt-1 text-sm opacity-60">
                    <span>{props.data.twint_count} payments</span>
                    <span>
                      {formatPriceInLocale(
                        props.data.twint_amount,
                        true,
                        2,
                        props.currency,
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Cash */}
              <div>
                <div class="flex justify-between mb-1">
                  <div class="flex items-center gap-2 text-accent">
                    <span class="material-symbols-outlined">payments</span>
                    <span>Cash</span>
                  </div>
                  {totalPayments > 0 && (
                    <span class="text-sm text-accent">
                      {((props.data.cash_amount / totalPayments) * 100).toFixed(
                        1,
                      )}
                      %
                    </span>
                  )}
                </div>
                <progress
                  class="progress progress-primary w-full"
                  value={props.data.cash_amount}
                  max={totalPayments}
                ></progress>
                <div class="flex justify-between mt-1 text-sm opacity-60">
                  <span>{props.data.cash_count} payments</span>
                  <span>
                    {formatPriceInLocale(
                      props.data.cash_amount,
                      true,
                      2,
                      props.currency,
                    )}
                  </span>
                </div>
              </div>

              <div>
                <div class="flex justify-between mb-1">
                  <div class="flex items-center gap-2 text-accent">
                    <span class="material-symbols-outlined">edit_square</span>
                    <span>Online</span>
                  </div>
                  {totalPayments > 0 && (
                    <span class="text-sm text-accent">
                      {(
                        ((props.data.online_amount || 0) / totalPayments) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  )}
                </div>
                <progress
                  class="progress progress-accent w-full"
                  value={props.data.online_amount}
                  max={totalPayments}
                ></progress>
                <div class="flex justify-between mt-1 text-sm opacity-60">
                  <span>{props.data.online_count} payments</span>
                  <span>
                    {formatPriceInLocale(
                      props.data.online_amount,
                      true,
                      2,
                      props.currency,
                    )}
                  </span>
                </div>
              </div>
              <div>
                <div class="flex justify-between mb-1">
                  <div class="flex items-center gap-2 text-accent">
                    <span class="material-symbols-outlined">edit_square</span>
                    <span>Manual</span>
                  </div>
                  {totalPayments > 0 && (
                    <span class="text-sm text-accent">
                      {(
                        ((props.data.manual_amount || 0) / totalPayments) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  )}
                </div>
                <progress
                  class="progress progress-accent w-full"
                  value={props.data.manual_amount}
                  max={totalPayments}
                ></progress>
                <div class="flex justify-between mt-1 text-sm opacity-60">
                  <span>{props.data.manual_count} payments</span>
                  <span>
                    {formatPriceInLocale(
                      props.data.manual_amount,
                      true,
                      2,
                      props.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
